import { Controller } from 'react-hook-form'
import { PatternFormat } from 'react-number-format'
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  OutlinedInput,
  IconButton,
  FormHelperText,
  TextField,
} from '@material-ui/core'
import { Close, ErrorOutline } from '@material-ui/icons'
import { useAutoFillAddressForm } from '@/hooks/components/address-validation/useAutoFillAddressForm'
import { State } from '@/types/data/states'
import { AddressValues, PreviousAddressDetailsValues, VerifyFreeformAddressResponse } from '@/types/v2/address'
import styles from './styles.module.css' 
import SpDropdown from '../SpDropDown'
import Autocomplete from '@material-ui/lab/Autocomplete'
import ExpandMoreRounded from '@material-ui/icons/ExpandMoreRounded'
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'
import useAddressAutocomplete from '@/hooks/useAddressAutocomplete'

type ComponentProps = {
  /**
   * Unique id value
   */
  id: string
  /**
   * Optional dialog title
   */
  dialogTitle?: string
  /**
   * Optional previous values
   */
  previousValues?: PreviousAddressDetailsValues
  /**
   * States list
   */
  states: State[]
  /**
   * Callback to handle onConfirm actions
   */
  onConfirm?: (suggestion: VerifyFreeformAddressResponse, extraInfo: string) => void
  /**
   * Callback to handle onCancel actions
   */
  onCancel: VoidFunction
  /*** 
   * 
   */
  setFormValues:(params: AddressValues)=>void
  /***
   * 
   */
  setIsAdding?:Dispatch<SetStateAction<boolean>>
  /***
   * 
   */
  watch?:any
  /***
   * 
   */
  setValue?:any
  /***
   * 
   */
  getValues?:any
}

export const AutoFillVerificationDialog = ({
  id,
  dialogTitle,
  states,
  previousValues, 
  onCancel,
  setFormValues,
  setIsAdding,
  setValue:setValueContext,
  getValues:getValueContext,
  watch
}: ComponentProps) => {
  const {
    formMethods: {
      control,
      formState: { errors, isSubmitting, isDirty, isValid },
      getValues, 
      register,
      handleSubmit,
      setValue,
    },
    // suggestion,
    onSubmit,
    handleChange 
  } = useAutoFillAddressForm({ states, previousValues,setIsAdding,setFormValues,setValueContext})
 
  const { addressOptions, setSearchQuery, searchQuery, fetchAddresses, searchLocalAddresses, setLoading, loading, freeSolo, setFreeSolo } = useAddressAutocomplete(); 
  const [selectedAddress,setSelectedAddress] = useState(null)
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);
  const [prevInputValue, setPrevInputValue] = useState(null);
  const [streetError, setStreetError] = useState<string | null>(null);
  const [isFirstAttemptDone, setIsFirstAttemptDone] = useState<boolean>(false);

  const handleOptionChange = (event: React.ChangeEvent<Element>, selectedOption: any | null) => {  
    if (selectedOption) { 
        const found = states.find(({ code }) => (selectedOption.state) === code);
        setValue('street',selectedOption.street)
        setValue('city',selectedOption.city)
        setValue('stateId',found.id)
        setValue('zipCode',selectedOption.zipcode)
        setSearchQuery(selectedOption.street)
        setValue('is_verified',true) 
        setSelectedAddress(selectedOption)
    }  
    else{ 
      setValue('street',"")
      setSearchQuery("")
      setValue('is_verified',false)
      setSelectedAddress(null) 
    }
  };

  const handleInputChange = (event: React.ChangeEvent<Element>, newInputValue: string) => {
    const trimmedNewInputValue = newInputValue.trim(); 
    
    setIsFirstAttemptDone(true)
  
    if (trimmedNewInputValue === prevInputValue) {
      setSearchQuery(newInputValue);   
      return;  
    }

    setSearchQuery(newInputValue);  
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);   
    }
  
    setPrevInputValue(trimmedNewInputValue);

    const localySearchedAddresses = searchLocalAddresses(trimmedNewInputValue)
    if (localySearchedAddresses.length>0){
      return
    }

    if (newInputValue == "") {
      return;
    }

    setStreetError(null); // Clear error when valid

    setLoading(true)
    
    const newTimeout = setTimeout(() => {
      
      const words = trimmedNewInputValue.split(/\s+/);
      const hasNumber = /\d/.test(trimmedNewInputValue);

      if (!hasNumber) {
        setLoading(false)
        setStreetError("Address should start with a number. (e.g. 123 Main St)");
        return;
      }

      if (trimmedNewInputValue.length >= 5 && words.length < 2) {
        setLoading(false)
        setStreetError("Address number and name should have space inbetween. (e.g. 123 Main St)");
        return;
      }

      if (!/^(?=.*[A-Za-z])(?=.*\d).+$/.test(trimmedNewInputValue)) {
        setLoading(false)
        setStreetError("Address should number and name. (e.g. 123 Main St)");
        return;
      }
      fetchAddresses(trimmedNewInputValue)
    }, 1000);

    setDebounceTimeout(newTimeout);   
  }

  const updateAddressVerified=()=>{  
    setValue('is_verified',false)
  }
     
  useEffect(() => { 
      setSearchQuery(getValues('street') || ""); 
      setValue('is_verified',getValueContext('is_verified')||false)
  }, []);
  
  return (
    <Dialog
      open
      PaperProps={{
        style: {
          padding: '24px',
          borderRadius: '9px',
          width: 620,
        },
      }}
    >
      {states.length === 0 ? (
        <CircularProgress size={20} />
      ) : (
        <>
          <div style={{ position: 'absolute', right: 15, top: 15 }}>
            <IconButton size="small" onClick={onCancel}>
              <Close />
            </IconButton>
          </div>
          <DialogTitle className={styles.dialogTitle}>{dialogTitle ?? 'Add New Address'}</DialogTitle>
          <DialogContent style={{ padding: 0 }}>
            <form onSubmit={handleSubmit(onSubmit)} id={id}>
              <div className={styles.street}>
                <label>Street Address</label> 
                <small style={{ color: '#f44336',  display: 'block' }}>{streetError}</small>
                <Controller
                  name="street"
                  control={control} 
                  rules={{ required: 'Street is required' }}
                  render={({ field }) => (
                    <Autocomplete
                      classes={{
                        inputRoot: styles.autocomplete, 
                        input: styles.autocompleteInput,
                      }} 
                      className={styles.street} 
                      popupIcon={<ExpandMoreRounded />}
                      options={addressOptions||[]}
                      onChange={handleOptionChange} 
                      getOptionLabel={(option) => `${option.street} ${option.city}, ${option.state} ${option.zipcode}${option.entries > 0 ? ` (${option.entries})` : ''}`}
                      value={field.value||null}
                      inputValue={searchQuery}
                      loading={loading}
                      noOptionsText={(searchQuery && isFirstAttemptDone) ? "No addresses found" : ""}
                      freeSolo={freeSolo}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Search Addres (e.g., 123 Main St)"
                          value={searchQuery} 
                          onChange={(e) => { 
                            setFreeSolo(false)
                            const newValue = e.target.value;
                            setSearchQuery(newValue); 
                            setValue('street',newValue);
                            handleInputChange(e, newValue);
                            field.onChange(newValue);
                          }}
                          onFocus={(e) => {
                            setFreeSolo(true)
                            const newValue = e.target.value
                            handleInputChange(e, newValue)
                          }}
                          onBlur={() => setFreeSolo(false)}
                        />
                      )} 
                    />
                  )}
                />
                {Boolean(errors?.street?.message) && (
                  <FormHelperText error>{errors?.street.message}</FormHelperText>
                )}
              </div>
              <div className={styles.grid1}>
                <div className={styles.apt}>
                <label>Apartment, Suite, Etc (Optional)</label>
                <OutlinedInput
                  {...register('extraInfo')}
                  fullWidth
                  placeholder="e.g. Apt #23" 
                />
                </div>
                <div className={styles.city}>
                  <label>City</label>
                  <OutlinedInput
                    {...register('city')}
                    placeholder="e.g. San Francisco"
                    error={Boolean(errors?.city?.message)}
                    endAdornment={
                      errors?.city?.message ? <ErrorOutline style={{ color: '#f44336' }} /> : undefined
                    }
                    onChange={(e)=> setValue('city',e.target.value)}
                    onKeyDown={updateAddressVerified}
                    disabled={selectedAddress}
                  />
                  {Boolean(errors?.city?.message) && (
                    <FormHelperText error>{errors?.city.message}</FormHelperText>
                  )}
                </div>
              </div>
              <div className={styles.gridDetails}>
                <div className={styles.state}>
                  <label>State</label>
                  <Controller
                    name="stateId"
                    control={control}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                      return (
                        <>
                          <SpDropdown
                            onChange={(value)=>{if(value){
                              const found = states.find(({ id }) => Number(value) === id)
                              setValue('stateId', found.id)
                            }}}
                            onBlur={onBlur}
                            value={value}
                            error={Boolean(error?.message)}
                            options={states}
                            valueKeyName="id"
                            labelKeyName="code"
                            placeholder="e.g. CA"
                            name="stateId"
                            zIndex={1500}
                            onKeyDown={updateAddressVerified}
                            isDisabled={selectedAddress}
                          />
                        </>
                      )
                    }}
                  />
                </div>
                <div className={styles.zipCode}>
                  <label>Zip Code</label>
                  <Controller
                    name="zipCode"
                    control={control}
                    render={({ field }) => (
                      <PatternFormat
                        {...field}
                        mask=""
                        placeholder="e.g. 94130"
                        format="##### ####"
                        error={Boolean(errors?.zipCode?.message)}
                        customInput={OutlinedInput}
                        onKeyDown={updateAddressVerified}
                        onValueChange={({ value }) => {  
                          setValue('zipCode', value) 
                        }
                        }
                        endAdornment={
                          errors?.zipCode?.message ? <ErrorOutline style={{ color: '#f44336' }} /> : undefined
                        }
                        disabled={selectedAddress}
                      />
                    )}
                  />
                  {Boolean(errors?.zipCode?.message) && (
                    <FormHelperText error style={{ marginBottom: 20 }}>
                      {errors?.zipCode.message}
                    </FormHelperText>
                  )}
                </div>
                <div className={styles.county}>
                <label>County</label>
                <OutlinedInput
                  {...register('county')}
                  fullWidth
                  placeholder="e.g. Queens"
                  onChange={(e)=> setValue('county',e.target.value)}
                  error={Boolean(errors?.county?.message)}
                  endAdornment={
                    errors?.county?.message ? <ErrorOutline style={{ color: '#f44336' }} /> : undefined
                  } 
                />
                  {Boolean(errors?.county?.message) && (
                    <FormHelperText error>{errors?.county.message}</FormHelperText>
                  )}
                </div>
              </div>

              <div className={styles.addButtonContainer}>
                <Button
                  type="submit"
                  variant="outlined"
                  color="primary"
                  // disabled={!isDirty || !isValid}
                   className={styles.customButton}
                >
                   Add
                </Button>
              </div>
            </form>
          </DialogContent> 
        </>
      )}
    </Dialog>
  )
}